/**
 * Mock data for UI development before the Rust backend lands.
 *
 * Values are taken from a real OpenADS run of `sub-02e8eb42` so the UI is laid
 * out against realistic magnitudes, column names and file naming. Do not
 * "clean up" the odd casing here — `_VASCULAR_QFV.csv` (DWI) really does differ
 * from `_Vascular_HPQFV.csv` (PWI) on disk.
 */

import {
  DWI_STAGES,
  DWI_STAGE_TITLES,
  PWI_STAGE_TITLES,
  type CaseDraft,
  type CaseManifest,
  type CommandPreview,
  type DockerStatus,
  type GpuStatus,
  type JobHandle,
  type JobLogEvent,
  type LogLevel,
  type JobProgressEvent,
  type JobSpec,
  type JobStageEvent,
  type JobStateEvent,
  type JobStatus,
  type LesionLoadTable,
  type NiftiHeaderInfo,
  type OutputNode,
  type Preferences,
  type QfvTable,
  type RecentCase,
  type ResultsSummary,
  type StageId,
  type StageStatus,
  type SubjectScan,
  type SystemStatus,
  EngineStatus,
  NativeEngineProbe,
} from "./contract";

// ------------------------------------------------------------- event bus ---

type Handler<T> = (payload: T) => void;

class MockBus {
  private handlers = new Map<string, Set<Handler<unknown>>>();

  on<T>(topic: string, fn: Handler<T>): () => void {
    const set = this.handlers.get(topic) ?? new Set();
    set.add(fn as Handler<unknown>);
    this.handlers.set(topic, set);
    return () => set.delete(fn as Handler<unknown>);
  }

  emit<T>(topic: string, payload: T): void {
    this.handlers.get(topic)?.forEach((fn) => fn(payload));
  }
}

export const mockBus = new MockBus();

// ---------------------------------------------------------------- system ---

export const dockerStatus = (): DockerStatus => ({
  available: true,
  version: "27.3.0",
  daemonRunning: true,
  error: null,
  images: [
    {
      reference: "openads:gpu",
      imageId: "sha256:c0cfc5d32856aa11",
      sizeBytes: 11_400_000_000,
      createdIso: "2026-07-22T09:14:00Z",
      flavor: "gpu",
    architecture: "amd64",
    emulated: false,
    },
    {
      reference: "openads:cpu",
      imageId: "sha256:37d012df7e62bb02",
      sizeBytes: 3_740_000_000,
      createdIso: "2026-07-22T08:02:00Z",
      flavor: "cpu",
    architecture: "amd64",
    emulated: true,
    },
  ],
  selectedImage: "openads:gpu",
  gpuRuntimeAvailable: true,
});

export const gpuStatus = (): GpuStatus => ({
  available: true,
  devices: [
    { index: 0, name: "NVIDIA RTX A5000", memoryTotalMb: 24564, memoryUsedMb: 812, utilizationPct: 3 },
    { index: 1, name: "NVIDIA RTX A5000", memoryTotalMb: 24564, memoryUsedMb: 4, utilizationPct: 0 },
  ],
  driverVersion: "550.144.03",
  cudaVersion: "12.4",
  error: null,
});

export const nativeProbe = (): NativeEngineProbe => ({
  ok: true,
  pythonPath: "/Users/you/Library/Application Support/org.openads.workstation/engine/venv/bin/python3",
  pythonVersion: "3.12.4",
  checkoutPath: "/Users/you/OpenADS",
  adsImportable: true,
  packages: { torch: "2.5.1", ants: "0.5.4", nibabel: "5.3.2", numpy: "2.0.1" },
  missing: [],
  cudaAvailable: false,
  mpsAvailable: true,
  modelsDir: "/Users/you/OpenADS/assets/models",
  atlasesDir: "/Users/you/OpenADS/assets/atlases/JHU_ICBM",
  cpuThreads: 10,
  error: null,
  remediation: [],
});

export const engineStatus = (): EngineStatus => ({
  kind: "native",
  selection: "auto",
  ready: true,
  deviceClass: "cpu",
  deviceLabel: "Local Python · CPU (10 threads) — OpenADS selects CUDA or CPU only, so MPS is unused",
  error: null,
  remediation: [],
  native: nativeProbe(),
  docker: dockerStatus(),
});

export const engineCandidates = () => ({
  pythons: [
    {
      path: "/Users/you/Library/Application Support/org.openads.workstation/engine/venv/bin/python3",
      version: "3.12.4",
      origin: "managed-venv" as const,
      usable: true,
      note: "",
    },
  ],
  checkouts: [
    {
      path: "/Users/you/OpenADS",
      origin: "well-known" as const,
      hasRunners: true,
      hasConfigs: true,
      hasModels: true,
      hasAtlases: true,
      missing: [],
    },
  ],
});

export const systemStatus = (): SystemStatus => ({
  docker: dockerStatus(),
  gpu: gpuStatus(),
  // One, as this machine works out: four performance cores, and a run wants all
  // four of them — see `concurrency::budget` for the measurements.
  concurrency: {
    limit: 1,
    automatic: 1,
    threadsPerJob: 4,
    reason:
      "One at a time: 4 performance cores, and a run wants all 4 of them. Two at once measured 22% faster but shifted lesion volumes by about 2%.",
  },
  storage: [
    { path: "/", freeBytes: 21_000_000_000, totalBytes: 1_800_000_000_000 },
    { path: "/media/joshua/PortableSSD", freeBytes: 140_000_000_000, totalBytes: 1_900_000_000_000 },
  ],
  appVersion: "0.1.0",
  engine: engineStatus(),
  // The dev machine this mock stands in for is a Mac; saying "linux" here sent
  // the platform-specific UI down the wrong branch under VITE_IPC_MOCK=on.
  platform: "macos",
});

// ------------------------------------------------------------ input scan ---

export const niftiHeader = (path: string): NiftiHeaderInfo => ({
  path,
  dim: [128, 128, 72, 50],
  pixdim: [1.72, 1.72, 3.0, 1.0],
  datatype: "int16",
  orientation: "RAS+",
  sformCode: 1,
  qformCode: 1,
  scaleSlope: 1,
  scaleInter: 0,
  isCompressed: path.endsWith(".gz"),
  affine: [
    [1, 0, 0, 0],
    [0, 1, 0, 0],
    [0, 0, 1, 0],
    [0, 0, 0, 1],
  ],
  volumeCount: 50,
  voxelVolumeMm3: 1.72 * 1.72 * 3,
});

export const subjectScan = (subjectDir: string): SubjectScan => ({
  subjectDir,
  subjectId: "sub-02e8eb42",
  baseId: "02e8eb42",
  files: [
    {
      path: `${subjectDir}/02e8eb42_DWI.nii.gz`,
      fileName: "02e8eb42_DWI.nii.gz",
      relPath: "02e8eb42_DWI.nii.gz",
      fileType: "NIfTI",
      modality: "DWI",
      sizeBytes: 1_678_022,
      dimensions: [128, 128, 72],
      voxelSizeMm: [1.72, 1.72, 3.0],
      datatype: "int16",
      status: "ok",
      detail: "OK",
    },
    {
      path: `${subjectDir}/02e8eb42_b0.nii.gz`,
      fileName: "02e8eb42_b0.nii.gz",
      relPath: "02e8eb42_b0.nii.gz",
      fileType: "NIfTI",
      modality: "b0",
      sizeBytes: 2_096_437,
      dimensions: [128, 128, 72],
      voxelSizeMm: [1.72, 1.72, 3.0],
      datatype: "int16",
      status: "ok",
      detail: "OK",
    },
    {
      path: `${subjectDir}/02e8eb42_PWI.nii.gz`,
      fileName: "02e8eb42_PWI.nii.gz",
      relPath: "02e8eb42_PWI.nii.gz",
      fileType: "NIfTI",
      modality: "PWI",
      sizeBytes: 23_930_885,
      dimensions: [128, 128, 36, 60],
      voxelSizeMm: [1.72, 1.72, 5.0],
      datatype: "int16",
      status: "ok",
      detail: "OK",
    },
    {
      path: `${subjectDir}/02e8eb42_PWI.json`,
      fileName: "02e8eb42_PWI.json",
      relPath: "02e8eb42_PWI.json",
      fileType: "JSON",
      modality: "PWI",
      sizeBytes: 2376,
      dimensions: null,
      voxelSizeMm: null,
      datatype: null,
      status: "ok",
      detail: "Sidecar metadata present",
    },
    {
      path: `${subjectDir}/02e8eb42_stroke.nii.gz`,
      fileName: "02e8eb42_stroke.nii.gz",
      relPath: "02e8eb42_stroke.nii.gz",
      fileType: "NIfTI",
      modality: "Stroke",
      sizeBytes: 11_650,
      dimensions: [128, 128, 72],
      voxelSizeMm: [1.72, 1.72, 3.0],
      datatype: "uint8",
      status: "ok",
      detail: "Ground truth found; metrics will be computed",
    },
    {
      path: `${subjectDir}/02e8eb42_ADC.nii.gz`,
      fileName: "02e8eb42_ADC.nii.gz",
      relPath: "02e8eb42_ADC.nii.gz",
      fileType: "NIfTI",
      modality: "ADC",
      sizeBytes: 0,
      dimensions: null,
      voxelSizeMm: null,
      datatype: null,
      status: "warning",
      detail: "Not found; will be computed from DWI and b0",
    },
  ],
  validCount: 5,
  warningCount: 1,
  errorCount: 0,
  isDatasetRoot: false,
  readiness: {
    dwi: { ready: true, missing: [], notes: ["DWI is 3D; b0 found", "ADC will be computed from DWI and b0"] },
    pwi: { ready: true, missing: [], notes: [] },
    combined: { ready: true, missing: [], notes: [] },
  },
});

// ------------------------------------------------------------------ case ---

const MOCK_OUTPUT_ROOT = "/media/joshua/PortableSSD/ads_desktop_out";

export const caseManifest = (draft?: CaseDraft): CaseManifest => ({
  schemaVersion: 1,
  caseId: "case-mock-0001",
  subjectId: draft?.subjectId ?? "sub-02e8eb42",
  studyName: draft?.studyName ?? "Acute Stroke Study",
  pipelineKind: draft?.pipelineKind ?? "dwi",
  createdIso: "2026-09-03T10:17:39Z",
  modifiedIso: "2026-09-03T10:23:06Z",
  inputRoot: "/home/joshua/Documents/wt_final/assets/examples/dwi",
  subjectRelPath: "sub-02e8eb42",
  pwiSubjectRelPath: null,
  outputRoot: draft?.outputRoot ?? MOCK_OUTPUT_ROOT,
  gpuIndex: draft?.gpuIndex ?? "0",
  dockerImage: null,
  stages: draft?.stages ?? { dwi: [...DWI_STAGES], pwi: [] },
  cleanupEnabled: draft?.cleanupEnabled ?? true,
  notes: "",
  images: [
    { id: "img-dwi", label: "DWI", relPath: "sub-02e8eb42/DWI/preprocess/sub-02e8eb42_DWI.nii.gz", origin: "output", space: "native", kind: "image", derived: true, meta: {} },
    { id: "img-adc", label: "ADC", relPath: "sub-02e8eb42/DWI/preprocess/sub-02e8eb42_ADC.nii.gz", origin: "output", space: "native", kind: "image", derived: true, meta: {} },
    { id: "img-dwi-mni", label: "DWI (MNI152)", relPath: "sub-02e8eb42/DWI/registration/sub-02e8eb42_DWI_space-MNI152_affsyn.nii.gz", origin: "output", space: "MNI152", kind: "image", derived: true, meta: {} },
  ],
  masks: [
    { id: "msk-brain", label: "Brain Mask", relPath: "sub-02e8eb42/DWI/preprocess/sub-02e8eb42_DWIbrain-mask.nii.gz", origin: "output", space: "native", kind: "mask", derived: true, meta: {} },
    { id: "msk-core", label: "Stroke Core", relPath: "sub-02e8eb42/DWI/segment/sub-02e8eb42_stroke-mask_space-MNI152_affsyn.nii.gz", origin: "output", space: "MNI152", kind: "mask", derived: true, meta: { model: "DAGMNet_CH3" } },
  ],
  transforms: [
    { id: "tf-aff", label: "DWI → MNI (Affine)", relPath: "sub-02e8eb42/DWI/registration/sub-02e8eb42_aff_space-individual2MNI152.mat", origin: "output", space: "MNI152", kind: "transform", derived: true, meta: {} },
    { id: "tf-syn", label: "DWI → MNI (SyN)", relPath: "sub-02e8eb42/DWI/registration/sub-02e8eb42_warp_space-MNI1522MNI152.nii.gz", origin: "output", space: "MNI152", kind: "transform", derived: true, meta: {} },
  ],
  atlases: [],
  measurements: [
    { id: "qfv-aspects", label: "ASPECTS QFV", relPath: "sub-02e8eb42/DWI/reporting/sub-02e8eb42_ASPECTS_QFV.csv", origin: "output", space: "MNI152", kind: "table", derived: true, meta: {} },
  ],
  reports: [
    { id: "rep-txt", label: "Radiological Report", relPath: "sub-02e8eb42/DWI/reporting/sub-02e8eb42_automatic_radiological_report.txt", origin: "output", space: "unknown", kind: "report", derived: true, meta: {} },
  ],
});

export const recentCases = (): RecentCase[] => [
  { caseId: "case-mock-0001", subjectId: "sub-02e8eb42", studyName: "Acute Stroke Study", caseJsonPath: `${MOCK_OUTPUT_ROOT}/sub-02e8eb42/case.json`, pipelineKind: "dwi", modality: "DWI", lastModifiedIso: "2026-09-03T10:23:06Z", status: "completed", exists: true },
  { caseId: "case-mock-0002", subjectId: "sub-05a971ae", studyName: "Perfusion Analysis", caseJsonPath: `${MOCK_OUTPUT_ROOT}/sub-05a971ae/case.json`, pipelineKind: "combined", modality: "DWI+PWI", lastModifiedIso: "2026-09-02T15:28:00Z", status: "running", exists: true },
  { caseId: "case-mock-0003", subjectId: "sub-007", studyName: "Chronic Lesion Study", caseJsonPath: `${MOCK_OUTPUT_ROOT}/sub-007/case.json`, pipelineKind: "pwi", modality: "PWI", lastModifiedIso: "2026-09-01T09:14:00Z", status: "not_created", exists: false },
  { caseId: "case-mock-0004", subjectId: "sub-93e750c3", studyName: "Longitudinal Follow-up", caseJsonPath: `${MOCK_OUTPUT_ROOT}/sub-93e750c3/case.json`, pipelineKind: "dwi", modality: "DWI", lastModifiedIso: "2026-08-30T16:56:00Z", status: "failed", exists: true },
];

// ------------------------------------------------------------------- job ---

function stageStatuses(spec: JobSpec): StageStatus[] {
  const rows: StageStatus[] = [];
  for (const id of spec.stages.dwi) {
    rows.push({ id, modality: "DWI", title: DWI_STAGE_TITLES[id], state: "pending", startedIso: null, finishedIso: null, durationSec: null });
  }
  for (const id of spec.stages.pwi) {
    rows.push({ id, modality: "PWI", title: PWI_STAGE_TITLES[id], state: "pending", startedIso: null, finishedIso: null, durationSec: null });
  }
  return rows;
}

const mockJobs = new Map<string, JobStatus>();
const mockTimers = new Map<string, ReturnType<typeof setInterval>>();

export const previewCommand = (spec: JobSpec): CommandPreview => {
  const argv = [
    "docker", "run", "--rm", "--gpus", "all", "--user", "1004:1004",
    "-v", "/home/joshua/Documents/wt_final/assets/examples/dwi:/data:ro",
    "-v", `${MOCK_OUTPUT_ROOT}:/out`,
    spec.dockerImage ?? "openads:gpu",
    spec.pipelineKind,
    "--subject-path", "/data/sub-02e8eb42",
    "--stages", spec.stages.dwi.join(","),
    "--gpu", spec.gpuIndex,
    "--output-root", "/out",
  ];
  return { argv, display: argv.join(" "), engine: "docker", env: [] };
};

/** Drives a fake run so the Jobs panel and progress bar can be built. */
export const startJob = (spec: JobSpec): JobHandle => {
  const jobId = `job-mock-${Date.now()}`;
  const stages = stageStatuses(spec);
  const status: JobStatus = {
    jobId,
    caseId: spec.caseId,
    subjectId: "sub-mock-01",
    state: "running",
    engine: "docker",
    deviceLabel: "Docker · CPU",
    deviceClass: "cpu",
    containerId: "mockcontainer0001",
    startedIso: new Date().toISOString(),
    finishedIso: null,
    exitCode: null,
    progressPct: 0,
    currentStage: stages[0]?.id ?? null,
    currentModality: stages[0]?.modality ?? null,
    stages,
    errorMessage: null,
    health: null,
  };
  mockJobs.set(jobId, status);

  let index = 0;
  let seq = 0;
  // Batched to match the real backend, which flushes every ~100ms / ~50 lines.
  const emitLog = (line: string, level: LogLevel = "info") =>
    mockBus.emit<JobLogEvent>("job://log", {
      jobId,
      entries: [{ seq: seq++, ts: new Date().toISOString(), line, level }],
    });

  emitLog(`Running command: ${previewCommand(spec).display}`);

  const timer = setInterval(() => {
    if (index >= stages.length) {
      clearInterval(timer);
      mockTimers.delete(jobId);
      status.state = "completed";
      status.finishedIso = new Date().toISOString();
      status.exitCode = 0;
      status.progressPct = 100;
      status.currentStage = null;
      emitLog("✅ DWI Pipeline Finished Successfully");
      mockBus.emit<JobStateEvent>("job://state", { jobId, state: "completed", exitCode: 0, errorMessage: null });
      return;
    }

    const stage = stages[index];
    if (stage.state === "pending") {
      stage.state = "running";
      stage.startedIso = new Date().toISOString();
      status.currentStage = stage.id;
      status.currentModality = stage.modality;
      emitLog(`${"=".repeat(60)}`);
      emitLog(`STAGE ${index + 1}/${stages.length}: ${stage.title}`);
      mockBus.emit<JobStageEvent>("job://stage", { jobId, stage: stage.id, modality: stage.modality, state: "running" });
      return;
    }

    stage.state = "completed";
    stage.finishedIso = new Date().toISOString();
    stage.durationSec = 12 + index * 7;
    emitLog(`✓ ${stage.title} completed`);
    mockBus.emit<JobStageEvent>("job://stage", { jobId, stage: stage.id, modality: stage.modality, state: "completed" });
    index += 1;
    status.progressPct = Math.round((index / stages.length) * 100);
    mockBus.emit<JobProgressEvent>("job://progress", {
      jobId,
      progressPct: status.progressPct,
      currentStage: (stages[index]?.id as StageId) ?? null,
      health: null,
    });
  }, 1200);

  mockTimers.set(jobId, timer);
  return {
    jobId,
    command: previewCommand(spec).argv,
    logPath: `${MOCK_OUTPUT_ROOT}/logs/${jobId}.log`,
    engine: "docker",
    deviceLabel: "Docker · CPU",
    deviceClass: "cpu",
  };
};

export const cancelJob = (jobId: string): void => {
  const timer = mockTimers.get(jobId);
  if (timer) {
    clearInterval(timer);
    mockTimers.delete(jobId);
  }
  const status = mockJobs.get(jobId);
  if (status) {
    status.state = "cancelled";
    status.finishedIso = new Date().toISOString();
    status.stages.forEach((s) => {
      if (s.state === "running" || s.state === "pending") s.state = "cancelled";
    });
  }
  mockBus.emit<JobStateEvent>("job://state", { jobId, state: "cancelled", exitCode: null, errorMessage: null });
};

export const jobStatus = (jobId: string): JobStatus =>
  mockJobs.get(jobId) ?? {
    jobId,
    caseId: "case-mock-0001",
    subjectId: "sub-mock-01",
    state: "completed",
    engine: "docker",
    deviceLabel: "Docker · CPU",
    deviceClass: "cpu",
    containerId: null,
    startedIso: "2026-09-03T10:17:39Z",
    finishedIso: "2026-09-03T10:23:06Z",
    exitCode: 0,
    progressPct: 100,
    currentStage: null,
    currentModality: null,
    stages: stageStatuses({
      caseId: "case-mock-0001",
      pipelineKind: "dwi",
      stages: { dwi: [...DWI_STAGES], pwi: [] },
      gpuIndex: "0",
      dockerImage: null,
      cleanupEnabled: true,
    }).map((s) => ({ ...s, state: "completed", durationSec: 40 })),
    errorMessage: null,
    health: null,
  };

export const jobList = (): JobStatus[] => Array.from(mockJobs.values());

// --------------------------------------------------------------- outputs ---

export const outputs = (): { root: string; tree: OutputNode[] } => ({
  root: `${MOCK_OUTPUT_ROOT}/sub-02e8eb42`,
  tree: [
    {
      name: "DWI",
      relPath: "DWI",
      isDir: true,
      sizeBytes: 0,
      modifiedIso: "2026-09-03T10:23:06Z",
      kind: "other",
      children: [
        { name: "preprocess", relPath: "DWI/preprocess", isDir: true, sizeBytes: 0, modifiedIso: "", kind: "other", children: [] },
        { name: "registration", relPath: "DWI/registration", isDir: true, sizeBytes: 0, modifiedIso: "", kind: "other", children: [] },
        { name: "segment", relPath: "DWI/segment", isDir: true, sizeBytes: 0, modifiedIso: "", kind: "other", children: [] },
        { name: "reporting", relPath: "DWI/reporting", isDir: true, sizeBytes: 0, modifiedIso: "", kind: "other", children: [] },
      ],
    },
  ],
});

// --------------------------------------------------------------- results ---

export const qfvTable = (): QfvTable => ({
  atlas: "ASPECTS",
  subjectId: "sub-02e8eb42",
  volLog10Ml: 2.0436648,
  rois: [
    { name: "Caudate", qfv: 0.40537 },
    { name: "lentiform", qfv: 0.6331949 },
    { name: "IC", qfv: 0.6318737 },
    { name: "insula", qfv: 0.7196758 },
    { name: "M1", qfv: 0.1003662 },
    { name: "M2", qfv: 0.2051712 },
    { name: "M3", qfv: 0.1090439 },
    { name: "M4", qfv: 0.0324415 },
    { name: "M5", qfv: 0.7813164 },
    { name: "M6", qfv: 0.0644423 },
  ],
});

export const lesionLoadTable = (): LesionLoadTable => ({
  atlas: "ASPECTS",
  entries: [
    { name: "caudate_L", voxels: 1857 },
    { name: "caudate_R", voxels: 0 },
    { name: "lentiform_nucleus_L", voxels: 4269 },
    { name: "lentiform_R", voxels: 0 },
    { name: "IC_L", voxels: 4401 },
    { name: "IC_R", voxels: 0 },
    { name: "insular_ribbon_L", voxels: 10834 },
    { name: "insular_ribbon_R", voxels: 0 },
    { name: "M1_L", voxels: 4098 },
    { name: "M1_R", voxels: 14 },
    { name: "brain", voxels: 50460 },
  ],
});

export const resultsSummary = (caseId: string): ResultsSummary => ({
  caseId,
  subjectId: "sub-02e8eb42",
  pipelineKind: "combined",
  coreVolumeMl: 141.64,
  hypoperfusionVolumeMl: 278.8,
  mismatchVolumeMl: 137.16,
  mismatchRatio: 1.97,
  coreVolumeSpace: "MNI152",
  coreVolumeNativeMl: 91.85,
  hypoperfusionVolumeNativeMl: 189.82,
  mismatchVolumeNativeMl: 97.97,
  mismatchRatioNative: 2.07,
  aspects: 5,
  processingSeconds: 378,
  dwiMetrics: {
    mni: { dice: 0.8712, precision: 0.9776, sensitivity: 0.7858, sdr: 1.0, predVolumeVox: 140749, trueVolumeVox: 175110 },
    orig: { dice: 0.8812, precision: 0.9903, sensitivity: 0.7937, sdr: 1.0, predVolumeVox: 15931, trueVolumeVox: 19879 },
    mniPostproc: { dice: 0.8713, precision: 0.9778, sensitivity: 0.7857, sdr: 1.0, predVolumeVox: 140702, trueVolumeVox: 175110 },
    origPostproc: { dice: 0.8853, precision: 0.9886, sensitivity: 0.8015, sdr: 1.0, predVolumeVox: 16118, trueVolumeVox: 19879 },
  },
  pwiMetrics: null,
  regional: [
    { territory: "M1 (Left)", coreVolumeMl: 4.1, corePct: 2.9, hypoVolumeMl: 20.2, hypoPct: 14.3 },
    { territory: "M2 (Left)", coreVolumeMl: 7.0, corePct: 4.9, hypoVolumeMl: 24.8, hypoPct: 17.6 },
    { territory: "M3 (Left)", coreVolumeMl: 2.7, corePct: 1.9, hypoVolumeMl: 11.2, hypoPct: 7.9 },
    { territory: "Insula (Left)", coreVolumeMl: 10.8, corePct: 7.7, hypoVolumeMl: 15.1, hypoPct: 10.7 },
    { territory: "Lentiform (Left)", coreVolumeMl: 4.3, corePct: 3.0, hypoVolumeMl: 6.9, hypoPct: 4.9 },
    { territory: "Caudate (Left)", coreVolumeMl: 1.9, corePct: 1.3, hypoVolumeMl: 3.4, hypoPct: 2.4 },
    { territory: "M1 (Right)", coreVolumeMl: 0.0, corePct: 0.0, hypoVolumeMl: 0.1, hypoPct: 0.1 },
  ],
  qfvTables: [qfvTable()],
  lesionLoadTables: [lesionLoadTable()],
  figures: [
    { label: "DWI stroke (MNI152)", path: `${MOCK_OUTPUT_ROOT}/sub-02e8eb42/DWI/reporting/sub-02e8eb42_DWIstroke_space-MNI152.png` },
    { label: "DWI stroke (native)", path: `${MOCK_OUTPUT_ROOT}/sub-02e8eb42/DWI/reporting/sub-02e8eb42_DWIstroke.png` },
  ],
  reports: [
    { label: "Radiological Report", path: `${MOCK_OUTPUT_ROOT}/sub-02e8eb42/DWI/reporting/sub-02e8eb42_automatic_radiological_report.txt`, kind: "txt" },
    { label: "ASPECTS Interpretation", path: `${MOCK_OUTPUT_ROOT}/sub-02e8eb42/DWI/reporting/sub-02e8eb42_ASPECTS_report_interpretation.pdf`, kind: "pdf" },
  ],
  radiologicalReportText: [
    "-----------------------------------Automatic Radiological Report-----------------------------------",
    "Area of restricted diffusion is unilateral, within the left brain hemisphere, with 140.75 ml,",
    "in the territory of middle cerebral artery, Lateral Lenticulostriate.",
    "The area involves the following brain regions: basal ganglia, deep white matter, frontal lobe, insula, parietal lobe, temporal lobe.",
    "There is no hydrocephalus.",
    "The predicted MCA – ASPECTS is 5.",
  ].join("\n"),
  warnings: [],
});

// ----------------------------------------------------------- preferences ---

export const preferences = (): Preferences => ({
  defaultInputRoot: "/home/joshua/Documents/wt_final/assets/examples/dwi",
  defaultOutputRoot: MOCK_OUTPUT_ROOT,
  defaultGpuIndex: "0",
  defaultDockerImage: null,
  cleanupEnabled: true,
  theme: "system",
  linkViews: true,
  overlayOpacity: 0.65,
  overlayColormap: "warm",
  showLabels: true,
  confirmBeforeCancel: true,
  uiMode: "clinical",
  preferredEngine: "auto",
  engineCheckoutPath: null,
  enginePythonPath: null,
});
